import React from "react";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import SEO from "../../hooks/SEO";
import PageBanner from "../../components/PageBanner";
import SectionHeader from "../../components/SectionHeader";
import ServiceCard from "../../components/ServiceCard";
import CapabilityCard from "../../components/CapabilityCard";
import PortfolioCompanyCard from "../../components/PortfolioCompanyCard";
import CTASection from "../../components/CTASection";
import {
  portfolioCompanies,
  seoContent,
  serviceCards,
  siteMeta,
  supportCapabilities,
} from "../../data/mockData";

const Services = () => (
  <>
    <SEO
      title={seoContent.services.title}
      description={seoContent.services.description}
      name={siteMeta.name}
      type="website"
    />
    <PageBanner
      title="Services"
      description="Mining, petroleum services, international trade, construction, industrial support, environmental services, heavy equipment, and sustainability-related capabilities."
      image={siteMeta.bannerImage}
      breadcrumbs={[{ label: "Services" }]}
    />

    <Box className="site-section">
      <Container>
        <Paper
          sx={{
            p: { xs: 3, md: 3.8 },
            mb: { xs: 4.5, md: 5.5 },
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-card)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(252,248,242,0.96))",
          }}
        >
          <Grid
            container
            spacing={{ xs: 3, md: 4 }}
            alignItems="start"
            sx={{ width: "100%", m: 0 }}
          >
            <Grid item xs={12} lg={7}>
              <Box className="eyebrow">Service Lines</Box>
              <Typography variant="h3" sx={{ mt: 1.6, maxWidth: 640 }}>
                Core and Extended Services
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 1.8, maxWidth: 690 }}
              >
                Refwa's business lines include mining, petroleum services, and
                international trade, alongside construction, equipment,
                environmental, facilities, and sustainability-related support
                services.
              </Typography>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Grid container spacing={1.4} sx={{ width: "100%", m: 0 }}>
                {serviceCards.slice(0, 3).map((service) => (
                  <Grid item xs={12} sm={6} lg={12} key={service.id}>
                    <Box
                      sx={{
                        p: 1.8,
                        borderRadius: 0.5,
                        border: "1px solid var(--color-border)",
                        bgcolor: "rgba(244,238,228,0.58)",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.6 }}
                      >
                        Core service line.
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        <SectionHeader
          eyebrow="Core Service Lines"
          title="Core Services"
          description="Refwa's core services cover mining, petroleum services, international trade, and related industrial support."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {serviceCards.map((service) => (
            <Grid item xs={12} md={6} lg={4} key={service.id}>
              <ServiceCard {...service} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Box className="site-section site-section--white">
      <Container>
        <SectionHeader
          eyebrow="Additional Capability Areas"
          title="Project Support"
          description="Additional service areas cover construction, equipment, environmental work, facilities, and sustainability-related operations."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {supportCapabilities.map((capability) => (
            <Grid item xs={12} md={6} lg={4} key={capability.title}>
              <CapabilityCard {...capability} />
            </Grid>
          ))}
        </Grid>
        <Stack
          direction="row"
          spacing={1.2}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 3.5 }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "text.secondary", mr: 0.4 }}
          >
            Service areas:
          </Typography>
          {supportCapabilities.map((capability) => (
            <Typography
              key={capability.title}
              variant="body2"
              sx={{
                px: 1.45,
                py: 0.85,
                borderRadius: 999,
                border: "1px solid var(--color-border)",
                bgcolor: "#fffdf9",
                color: "text.secondary",
              }}
            >
              {capability.title}
            </Typography>
          ))}
        </Stack>
      </Container>
    </Box>

    <Box className="site-section">
      <Container>
        <SectionHeader
          eyebrow="Portfolio Companies"
          title="Group Companies"
          description="These companies reflect operating capacity across construction, environmental services, facilities, water, and public-sector support work."
        />
        <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
          {portfolioCompanies.map((company) => (
            <Grid item xs={12} md={6} lg={4} key={company.id}>
              <PortfolioCompanyCard {...company} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <CTASection
      title="Discuss Service Requirements"
      description="For service inquiries, please contact Refwa directly through the listed company details."
      primaryLabel="Contact Refwa"
      primaryTo="/contact"
      secondaryLabel="View Articles"
      secondaryTo="/articles"
    />
  </>
);

export default Services;
